/**
 * FILE: packages/supabase/src/internal/activity.ts
 * PURPOSE: Activity logging for audit trail. Every write service must call logActivity().
 * AGENT_CONTEXT: CRITICAL — Invariant: all writes must log activity. Never skip.
 * INVARIANTS: entity_id must be student phone for student-scoped operations.
 * LAST_REVIEWED: 2026-05-07
 */

import type { DbClient } from '../dbClient'
import { defaultDbClient } from '../dbClient'
import type { Json } from '../types'

export interface ActivityActor {
  userId: string
  userName: string
  userRole: string
}

export interface ActivityParams {
  userId: string
  userName: string
  userRole: string
  action: string
  entityType: string
  entityId: string
  oldValues?: Record<string, unknown>
  newValues?: Record<string, unknown>
}

export function withActivityActor(
  actor: ActivityActor,
  event: Omit<ActivityParams, 'userId' | 'userName' | 'userRole'>
): ActivityParams {
  return {
    ...actor,
    ...event,
  }
}

function safeJson(value: unknown): Json | null {
  try {
    return JSON.parse(JSON.stringify(value ?? null)) as Json | null
  } catch {
    return null
  }
}

/** `activity_log.user_id` is UUID; portal students use `actor_phone`. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const PHONE_PK_RE = /^[0-9]{10,15}$/

function activityActorForDb(userId: string): { user_id: string | null; actor_phone: string | null } {
  const trimmed = userId.trim()
  if (UUID_RE.test(trimmed)) {
    return { user_id: trimmed, actor_phone: null }
  }
  const digits = trimmed.replace(/\D/g, '')
  if (PHONE_PK_RE.test(digits)) {
    return { user_id: null, actor_phone: digits }
  }
  return { user_id: null, actor_phone: null }
}

export async function logActivity(
  params: ActivityParams,
  client: DbClient = defaultDbClient
) {
  try {
    const actor = activityActorForDb(params.userId)
    if (!actor.user_id && !actor.actor_phone) {
      return
    }
    const payload = {
      user_id: actor.user_id,
      actor_phone: actor.actor_phone,
      user_name: params.userName,
      user_role: params.userRole,
      action: params.action,
      entity_type: params.entityType,
      entity_id: params.entityId,
      old_values: safeJson(params.oldValues),
      new_values: safeJson(params.newValues),
    }
    const { error } = await client.from('activity_log').insert(payload)
    if (error) {
      console.error('Activity log failed:', error)
    }
  } catch (e) {
    console.error('Activity log exception:', e)
  }
}
